import { Img } from "@/components/site/Img";
import { useState } from "react";
import { MessageCircle, Play, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { SMS_DRAFTS } from "@/lib/contact";
import { useSmsHref } from "./ContactButtons";
import { VideoLightbox } from "./VideoLightbox";

const newsItems = [
  {
    id: "garden-centerpiece-install",
    tag: "Recent installation",
    title: "A new garden centerpiece",
    image: "/images/install-garden-centerpiece.jpg",
    alt: "Multi-piece garden fountain with statues and surround, installed by Chris",
    video: "install-garden-centerpiece.mp4",
    videoLabel: "Watch it run",
    clickToPlay: true,
    actions: "watch-only",
  },
  {
    id: "customer-install",
    tag: "Recent Installation",
    title: "Didn't buy it from us? Chris still installs it.",
    body: "This customer found their fountain elsewhere and called Chris to install it, surround and all. If you have a fountain that needs setting up, call Chris, or text a photo of it and the spot you have in mind.",
    image: "/images/install-customer-fountain.jpg",
    alt: "Customer-purchased fountain assembled by Chris with the outer surround in place",
    contain: true,
    link: { to: "/fountain-repair#installation", label: "See the installation" },
  },
  {
    id: "casting-away",
    tag: "Casting Away",
    title: "Casting away: fresh out of the molds.",
    body: "Buddhas, lions, a lucky elephant, and bowl planters, all fresh out of the molds and curing on the pad. Ask about current availability.",
    image: "/images/casting-away.jpg",
    alt: "Fresh cast concrete statues and planters curing on the pad at the Villa Rica shop",
  },
  {
    id: "fresh-casts",
    tag: "In The Molds Now",
    title: "A fresh round of casts, curing now.",
    body: "Take a quick walk through the shop. Molds are full and a new batch of pieces is setting up right now. Watch the video, then call or text to claim one before it hits the yard.",
    image: "/images/fresh-casts-poster.jpg",
    video: "fresh-casts.mp4",
    alt: "Concrete molds full and curing at the Villa Rica shop",
  },
  {
    id: "mold-column",
    tag: "Fresh Molds",
    title: "The ornamental column mold is ready.",
    body: "A tall carved column mold just came together on the bench. It makes a perfect pedestal for statues, sundials, or gazing balls. First pours are coming, call or text to claim one early.",
    image: "/images/mold-column.jpg",
    alt: "Tall ornamental column mold assembled at the Villa Rica shop",
  },
  {
    id: "foo-dogs",
    tag: "Fresh From The Mold",
    title: "Foo dog guardians, just cast.",
    body: "Chris just cast a pair of foo dogs, the legendary guardians of Asian tradition. Part lion, part dragon, always placed in pairs to watch over a gate or doorway. Call or text to claim the pair.",
    image: "/images/foo-dog-mold.jpg",
    alt: "Foo dog guardian statue fresh from the mold at the Villa Rica shop",
  },
  {
    id: "mold-koi",
    tag: "Fresh Molds",
    title: "The leaping koi mold is here.",
    body: "A koi fountain topper, caught mid leap. The first pours are coming soon, call or text to claim one early.",
    image: "/images/mold-koi.jpg",
    alt: "Leaping koi fountain topper mold at the Villa Rica shop",
  },
  {
    id: "mold-vase",
    tag: "Fresh Molds",
    title: "A new vase mold on the bench.",
    body: "A tall paneled vase mold just joined the lineup. Watch for the first fresh castings in the yard.",
    image: "/images/mold-vase.jpg",
    alt: "Tall paneled vase planter mold at the Villa Rica shop",
  },
];

const NewsCard = ({ item }) => {
  const itemSms = useSmsHref(SMS_DRAFTS.general + item.title + ".");
  const [showVideo, setShowVideo] = useState(false);
  return (
    <article
      data-testid={`news-item-${item.id}`}
      className="card-lift border border-[#3A3835] bg-[#26241F] flex flex-col"
    >
      <div className="relative w-full aspect-[4/3] overflow-hidden bg-black/30">
        <Img src={item.image} alt={item.alt} loading="lazy" sizes="(min-width: 1024px) 30vw, (min-width: 640px) 50vw, 100vw" className={`w-full h-full ${item.contain ? "object-contain" : "object-cover object-top"}`} />
        <span className="absolute top-3 left-3 px-3 py-1.5 bg-[#C8B387] text-[#1F1E1C] text-[10px] tracking-[0.28em] uppercase">
          {item.tag}
        </span>
        {item.video && (
          <button
            type="button"
            onClick={() => setShowVideo(true)}
            data-testid={`news-${item.id}-play`}
            aria-label={`Watch: ${item.title}`}
            className="absolute inset-0 flex items-center justify-center"
          >
            <span className="w-14 h-14 rounded-full bg-[#1F1E1C]/70 border border-white/60 text-white flex items-center justify-center backdrop-blur-sm transition-transform duration-300 hover:scale-110">
              <Play size={20} strokeWidth={1.5} fill="currentColor" className="ml-0.5" />
            </span>
          </button>
        )}
        {item.video && (
          <VideoLightbox
            open={showVideo}
            onClose={() => setShowVideo(false)}
            autoplay={!item.clickToPlay}
            video={{
              mp4: `${process.env.REACT_APP_BACKEND_URL}/api/media/${item.video}`,
              poster: item.image,
              title: item.title,
              portrait: !item.landscape,
            }}
          />
        )}
      </div>
      <div className="p-6 flex flex-col flex-1">
        <h3 className="font-serif font-light text-[#F9F6F0] text-xl md:text-2xl leading-snug tracking-tight mb-3">
          {item.title}
        </h3>
        {item.body && <p className="text-[#B8B3AA] text-sm leading-relaxed mb-6 flex-1">{item.body}</p>}
        {item.actions === "watch-only" ? (
          <button
            type="button"
            onClick={() => setShowVideo(true)}
            data-testid={`news-${item.id}-watch`}
            className="inline-flex items-center gap-2 min-h-[44px] mt-auto text-[#C8B387] hover:text-[#E8DFC9] text-xs tracking-[0.22em] uppercase transition-colors"
          >
            <Play size={13} strokeWidth={1.5} /> {item.videoLabel || "Watch the video"}
          </button>
        ) : item.link ? (
          <Link
            to={item.link.to}
            data-testid={`news-${item.id}-link`}
            className="inline-flex items-center gap-2 text-[#C8B387] hover:text-[#E8DFC9] text-xs tracking-[0.22em] uppercase transition-colors"
          >
            {item.link.label} <ArrowRight size={14} strokeWidth={1.5} />
          </Link>
        ) : (
        <a
          href={itemSms}
          data-testid={`news-${item.id}-message`}
          aria-label={`Text Chris about ${item.title}`}
          className="inline-flex items-center gap-2 text-[#C8B387] hover:text-[#E8DFC9] text-xs tracking-[0.22em] uppercase transition-colors"
        >
          <MessageCircle size={14} strokeWidth={1.5} /> Text about this
        </a>
        )}
      </div>
    </article>
  );
};

export default function GfysNews() {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? newsItems : newsItems.slice(0, 3);
  return (
    <section
      id="gfys-news"
      data-testid="gfys-news-section"
      className="bg-[#1F1E1C] py-10 md:py-12"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="flex items-end justify-between gap-6 mb-8 md:mb-10">
          <div>
            <div className="section-eyebrow mb-4">
              <span className="text-[#C8B387]">GFYS News</span>
            </div>
            <h2 className="font-serif font-light text-[#F9F6F0] text-3xl md:text-4xl tracking-tight">
              Fresh from the shop, and recent installs.
            </h2>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {visible.map((item) => (
            <NewsCard key={item.id} item={item} />
          ))}
        </div>
        {newsItems.length > 3 && (
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              data-testid="news-toggle-button"
              className="inline-flex items-center gap-2 text-[#C8B387] hover:text-[#E8DFC9] text-xs tracking-[0.22em] uppercase border-b border-[#C8B387]/40 hover:border-[#E8DFC9] pb-1 transition-colors"
            >
              {showAll ? "Show fewer updates" : `See older updates (${newsItems.length - 3} more)`}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
